import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../../common/reduxQuery/helpers";
import { Translation } from "../../configMap";
import {
  DocumentTypeHoldReasons,
  DocumentTypeHoldReasonsUpdate,
  HoldReasonListItem,
} from "../interfaces/holdReasons";

export const holdReasonsApi = createApi({
  reducerPath: "holdReasonsApi",
  baseQuery: customBaseQuery(),
  tagTypes: ["HoldReasons", "DocumentTypeHoldReasons"],
  endpoints: (build) => ({
    getOrganizationHoldReasons: build.query<Array<HoldReasonListItem>, string>({
      query: (organizationId) => ({
        url: `/api/v1/hold-reason?organization-id=${organizationId}`,
        method: "GET",
      }),
      providesTags: ["HoldReasons"],
    }),
    createHoldReason: build.mutation<
      HoldReasonListItem,
      { organizationId: string; translations: Translation }
    >({
      query: (body) => ({
        url: "/api/v1/hold-reason",
        body,
        method: "POST",
      }),
      invalidatesTags: ["HoldReasons"],
    }),
    updateHoldReason: build.mutation<
      HoldReasonListItem,
      { id: string; translations: Translation }
    >({
      query: ({ id, translations }) => ({
        url: `/api/v1/hold-reason/${id}`,
        body: { translations },
        method: "PUT",
      }),
      invalidatesTags: ["HoldReasons"],
    }),
    deleteHoldReason: build.mutation<void, string>({
      query: (id) => ({
        url: `/api/v1/hold-reason/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["HoldReasons"],
    }),
    getDocumentTypeHoldReasons: build.query<
      Array<DocumentTypeHoldReasons>,
      string
    >({
      query: (organizationId) => ({
        url: `/api/v1/hold-reason/document-types?organization-id=${organizationId}`,
        method: "GET",
      }),
      providesTags: ["DocumentTypeHoldReasons"],
    }),
    updateDocumentTypeHoldReasons: build.mutation<
      DocumentTypeHoldReasons,
      {
        organizationId: string;
        documentTypeId: string;
        update: DocumentTypeHoldReasonsUpdate;
      }
    >({
      query: ({ organizationId, documentTypeId, update }) => ({
        url: `/api/v1/hold-reason/document-types/${documentTypeId}?organization-id=${organizationId}`,
        body: update,
        method: "PUT",
      }),
      // Applied to the cache first so a switch flips under the finger instead of
      // after the round trip; rolled back if the save fails.
      async onQueryStarted(
        { organizationId, documentTypeId, update },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          holdReasonsApi.util.updateQueryData(
            "getDocumentTypeHoldReasons",
            organizationId,
            (draft) => {
              const documentType = draft.find(
                (item) => item.documentTypeId === documentTypeId,
              );

              if (!documentType) {
                return;
              }

              Object.assign(documentType, update);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      // Linking also changes which document types a reason reports, and that gates its delete.
      invalidatesTags: ["DocumentTypeHoldReasons", "HoldReasons"],
    }),
    getOnHoldCount: build.query<
      number,
      { documentTypeId: string; holdReasonId: string }
    >({
      query: ({ documentTypeId, holdReasonId }) => ({
        url: `/api/v1/hold-reason/document-types/${documentTypeId}/reasons/${holdReasonId}/on-hold-count`,
        method: "GET",
      }),
    }),
    unlinkHoldReason: build.mutation<
      DocumentTypeHoldReasons,
      {
        documentTypeId: string;
        holdReasonId: string;
        transferToHoldReasonId: string;
      }
    >({
      query: ({ documentTypeId, holdReasonId, transferToHoldReasonId }) => ({
        url: `/api/v1/hold-reason/document-types/${documentTypeId}/reasons/${holdReasonId}?transfer-to=${transferToHoldReasonId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["DocumentTypeHoldReasons", "HoldReasons"],
    }),
  }),
});

export const {
  useGetOrganizationHoldReasonsQuery,
  useCreateHoldReasonMutation,
  useUpdateHoldReasonMutation,
  useDeleteHoldReasonMutation,
  useGetDocumentTypeHoldReasonsQuery,
  useUpdateDocumentTypeHoldReasonsMutation,
  useLazyGetOnHoldCountQuery,
  useUnlinkHoldReasonMutation,
} = holdReasonsApi;
